// generateTip — pure Contextual_Tip generator for Trip_Steps.
//
// Maps a step's `stepType` (plus its source record and traveler context) to a
// single actionable advice string. Every returned tip is a non-empty string
// whose trimmed form begins with the literal prefix "Tip:" so the timeline can
// render it verbatim in the fourth column of a Trip_Step.
//
// Security tips reference the traveler's known clearances (TSA PreCheck,
// CLEAR), drawn from `ctx.userProfile` and/or `ctx.departureCalc.userPreferences`.
// Ground-transport tips reference timing, traffic, or driver contact details.
// Any unknown / unhandled step type falls back to a generic non-empty tip,
// guaranteeing the "Tip:"-prefix contract for every step.
//
// The function is total: it never throws for any input shape.
//
// Validates: Requirements 6.1, 6.2, 6.3, 6.4

const TIP_PREFIX = 'Tip:';

/**
 * Normalize the traveler's expedited-screening clearances from the several
 * shapes used across the codebase into a single flag set.
 *
 * Recognized sources (all optional):
 *   - ctx.userProfile.{tsaPreCheck, clear, globalEntry}
 *   - ctx.userProfile.travelerInfo.{tsaPreCheck, clear, globalEntry}
 *   - ctx.departureCalc.userPreferences.{hasPreCheck, hasClear, hasGlobalEntry}
 *
 * @param {Object} [ctx]
 * @returns {{ hasPreCheck: boolean, hasClear: boolean, hasGlobalEntry: boolean }}
 */
function resolveClearances(ctx) {
  const profile = ctx?.userProfile ?? {};
  const travelerInfo = profile?.travelerInfo ?? {};
  const prefs = ctx?.departureCalc?.userPreferences ?? {};

  const hasPreCheck = Boolean(
    profile.tsaPreCheck || travelerInfo.tsaPreCheck || prefs.hasPreCheck,
  );
  const hasClear = Boolean(
    profile.clear || travelerInfo.clear || prefs.hasClear,
  );
  const hasGlobalEntry = Boolean(
    profile.globalEntry || travelerInfo.globalEntry || prefs.hasGlobalEntry,
  );

  return { hasPreCheck, hasClear, hasGlobalEntry };
}

/**
 * Ensure a candidate string is non-empty and carries the "Tip:" prefix.
 * Guards every code path so the prefix contract (Req 6.2) always holds.
 *
 * @param {string} text  Guidance text without the prefix.
 * @param {string} fallback  Guidance used when `text` is empty/blank.
 * @returns {string}
 */
function withPrefix(text, fallback) {
  const body = typeof text === 'string' && text.trim() ? text.trim() : fallback;
  return `${TIP_PREFIX} ${body}`;
}

/**
 * Build the security tip, referencing whichever expedited clearances the
 * traveler is known to hold (Req 6.3).
 *
 * @param {Object} [ctx]
 * @returns {string}
 */
function securityTip(ctx) {
  const { hasPreCheck, hasClear, hasGlobalEntry } = resolveClearances(ctx);

  if (hasPreCheck && hasClear) {
    return withPrefix(
      'Use the CLEAR lane to verify your identity, then continue through TSA PreCheck — keep shoes and laptop in your bag.',
    );
  }
  if (hasClear) {
    return withPrefix(
      'Head to the CLEAR lane to skip the ID-check line and reach screening faster.',
    );
  }
  if (hasPreCheck) {
    return withPrefix(
      'Look for the TSA PreCheck lane — leave shoes, belt, and laptop in place to move through quickly.',
    );
  }
  if (hasGlobalEntry) {
    return withPrefix(
      'Your Global Entry membership includes TSA PreCheck — look for the PreCheck lane at the checkpoint.',
    );
  }
  return withPrefix(
    'Allow extra time for the standard security line, and have your ID and boarding pass ready before you reach the checkpoint.',
  );
}

/**
 * Build the ground-transport tip, referencing timing, traffic, or driver
 * contact details (Req 6.4).
 *
 * @param {Object} [source]  Car-rental / rideshare source record.
 * @returns {string}
 */
function groundTransportTip(source) {
  const driverPhone = source?.driverPhone || source?.driverContact || source?.phone;
  const confirmation = source?.confirmationNumber || source?.confirmation;

  if (driverPhone) {
    return withPrefix(
      `Check live traffic before your pickup window and contact your driver at ${driverPhone} if plans change.`,
    );
  }
  if (confirmation) {
    return withPrefix(
      `Check traffic before heading out and keep confirmation ${confirmation} handy for your driver.`,
    );
  }
  return withPrefix(
    'Check current traffic and leave a buffer for delays — have your driver or pickup contact details ready.',
  );
}

/**
 * Generate a Contextual_Tip for a Trip_Step.
 *
 * @param {string} stepType  One of the known step types
 *   ('leave_home' | 'parking' | 'security' | 'gate' | 'flight_arrival' |
 *    'ground_transport' | 'hotel_checkin' | 'hotel_checkout' | 'dining' |
 *    'meeting' | 'landing' | 'arrive_home'), or any value for the generic fallback.
 * @param {Object} [source]  The source record backing this step (flight, hotel, rental, etc.).
 * @param {Object} [ctx]     Traveler context: { userProfile, departureCalc, ... }.
 * @returns {string}  A non-empty string beginning (when trimmed) with "Tip:".
 */
export function generateTip(stepType, source = {}, ctx = {}) {
  switch (stepType) {
    case 'leave_home':
      return withPrefix(
        'Check traffic on your route to the airport before departing and leave a few minutes early.',
      );

    case 'parking':
      return withPrefix(
        'Note your parking lot, level, and spot, and factor in the shuttle wait when timing your arrival.',
      );

    case 'security':
      return securityTip(ctx);

    case 'gate':
      return withPrefix(
        'Confirm your gate on the airport screens, as gates can change — keep your boarding pass accessible.',
      );

    case 'ground_transport':
      return groundTransportTip(source);

    case 'flight_arrival':
    case 'landing':
      return withPrefix(
        'Turn off airplane mode on landing to receive baggage-claim and connection updates.',
      );

    case 'hotel_checkin':
      return withPrefix(
        'Have your confirmation number and ID ready at the front desk, and ask about your loyalty perks.',
      );

    case 'hotel_checkout':
      return withPrefix(
        'Review your folio for accuracy before checkout and ask the desk to store bags if you have time to spare.',
      );

    case 'dining':
      return withPrefix(
        'Arrive a few minutes early for your reservation and confirm any dress code before you head out.',
      );

    case 'meeting':
      return withPrefix(
        'Build in travel time to your meeting location and confirm the address before you leave.',
      );

    case 'arrive_home':
      return withPrefix(
        'Check traffic for your drive home and arrange your ride or pickup before you land.',
      );

    default:
      return withPrefix(
        'Review the details for this step and allow a little extra time to stay ahead of your schedule.',
      );
  }
}

export default generateTip;
